import React from "react";
import { FormattedMessage } from "react-intl";
import { Checkbox, FormControl, FormErrorMessage, Text } from "@chakra-ui/react";

import { PrivacyPolicyModalLinkButton, TOSModalLinkButton } from "./Modal";

/**
 * The type styles a <button> will not inherit from the sentence it sits in, restated as props for
 * the two link buttons: Chakra's `Button` takes its font size from its own `size` prop, and
 * `font-style` is not restored by Chakra's reset. This translation is why callers hand their
 * styling to `ConsentView` instead of styling the copy themselves.
 *
 * `color` is deliberately NOT among them: Chakra's `link` variant sets its own, so the links keep
 * the brand link colour instead of taking the copy's and ceasing to look clickable.
 * @param {object} copyProps - the style props applied to the sentence
 */
const linkPropsFor = ({ fontSize, fontStyle }) => ({ size: fontSize, fontStyle });

/**
 * The sentence's leading, declared rather than inherited because the control offset below is
 * computed from it. 1.5 is Chakra's global `lineHeights.base`, which is what the copy already
 * rendered at everywhere except the `/tos` page, whose wrapper sets 1.35em (`FullPage.jsx`).
 */
const LINE_HEIGHT = 1.5;

/**
 * Half the leading of the first line - the gap between the top of a line box and the top of the
 * glyphs in it. The copy wraps to two or three lines on every surface, and Chakra's Checkbox
 * centres its control against the whole label, so the box sat ~9px below the line it belongs to.
 * `alignItems: flex-start` lines it up with the top of the first LINE BOX; this brings it down onto
 * the text itself.
 *
 * Reaching into Chakra's control part is deliberate here, unlike for the type styles below: the
 * control has no prop for its own box, `styleConfig` would replace the Checkbox theme rather than
 * extend it, and offsetting the sentence instead would move the block, not the box inside it.
 *
 * In `em` so it tracks whatever type size the surface asked for - which is why `fontSize` is
 * declared on the Checkbox root as well as on the sentence: the control inherits from the root, not
 * from the span beside it. `1rem` is the control's own box (Chakra's md Checkbox, `sizes.4`); both
 * surfaces leave the Checkbox at its default size.
 */
const CONTROL_OFFSET = `calc((${LINE_HEIGHT}em - 1rem) / 2)`;

/**
 * The one place the ToS and Privacy destinations are chosen, so both can be flipped in a single
 * edit when the canonical URLs are reconciled.
 */
const tosLinkRenderers = (linkProps) => ({
  tos: (msg) => <TOSModalLinkButton label={msg} {...linkProps} />,
  privacy: (msg) => <PrivacyPolicyModalLinkButton label={msg} {...linkProps} />,
});

/**
 * PendingLegalCopy
 * @param {object} props
 * @param {object} [props.linkProps] - props for the two link buttons, from `linkPropsFor`
 * @description PLACEHOLDER consent wording: this is the currently approved `/tos` copy, now shown
 * on five surfaces rather than one. The final wording is still pending — BRD-015 §3.1.2 rewording,
 * the Thunes Financial Services LLC entity change, and §5.2 (DEV-7687) — and when it lands it is
 * swapped here and nowhere else. Tests assert on `data-testid` and the presence of the two link
 * buttons, never on the sentence.
 */
export const PendingLegalCopy = ({ linkProps }) => (
  <FormattedMessage
    defaultMessage="I agree to Tilia LLC's <tos>Terms of Service</tos> and acknowledge receipt of Tilia LLC's <privacy>Privacy Policy</privacy>"
    values={tosLinkRenderers(linkProps)}
    description="message with links"
  />
);

/**
 * ConsentView
 * @param {object} props
 * @param {boolean} [props.checked] - checkbox state
 * @param {Function} props.onChange - receives the raw change EVENT, not a boolean. The standalone
 * form hands this Formik's `field.onChange`, which keys off `e.target.name`, so the event must pass
 * through unadapted and the `name` attribute must stay `tosAndPpCheckbox`. Inline callers do the
 * boolean adaptation themselves.
 * @param {Function} [props.onBlur] - standalone passes Formik's `field.onBlur`; it is what sets
 * `meta.touched`, and therefore when the validation message appears.
 * @param {string|false} [props.error] - message to display. A single string prop reproduces the
 * previous behavior: the message used to render on `meta.error` alone, but Chakra's
 * `FormErrorMessage` only renders inside an invalid `FormControl`, so the observable condition was
 * already `touched && error`.
 * @param {...any} [copyProps] - Chakra style props for the consent sentence, so each surface can
 * set its own tone in place rather than through an enum here. `fontSize` and `fontStyle` are also
 * translated onto the two link buttons - see `linkPropsFor`. Passing none gives the `/tos` page's
 * full-size treatment.
 * @description Fully controlled presentation shared by the standalone `/tos` page and inline
 * consent. Styling is the only thing a surface varies: same wording, same checkbox, same error
 * placement either way, so the consent a user ticks mid-checkout is the same artifact as the one on
 * `/tos`. No state, no fetching, no submit button, no null return.
 */
export const ConsentView = ({ checked, onChange, onBlur, error, ...copyProps }) => {
  const isInvalid = Boolean(error);
  // `fontSize` is defaulted rather than left to inheritance so that the sentence always DECLARES
  // the size it is set in. The Checkbox label part's own size is `md` too, so this changes nothing
  // visible on `/tos` - but it means there is one value to pass to the links, instead of a case
  // where the copy inherits a size the links have to guess at.
  // `lineHeight` is declared for the same reason, and CONTROL_OFFSET assumes it: a caller
  // overriding it has to move the control with it.
  const copyStyles = { fontSize: "md", lineHeight: LINE_HEIGHT, ...copyProps };
  return (
    <FormControl isInvalid={isInvalid}>
      <Checkbox
        name="tosAndPpCheckbox"
        data-testid="tos-and-pp-checkbox"
        isChecked={Boolean(checked)}
        onChange={onChange}
        onBlur={onBlur}
        isInvalid={isInvalid}
        spacing="1rem"
        // The box belongs to the first line of the sentence, not to the middle of all of it - see
        // CONTROL_OFFSET. `fontSize` here is what makes that offset's `em` resolve against the
        // copy's size; the sentence below declares its own, so this reaches only the control.
        alignItems="flex-start"
        fontSize={copyStyles.fontSize}
        sx={{
          // The label part is the block that lays the sentence out, and Chakra gives it a font size
          // from the Checkbox's `size` - `md`, whatever the copy is set in. Its 24px strut, not the
          // copy's own 18px line box, was what `flex-start` lined the control up with, leaving the
          // box 4px above an `xs` sentence (measured in Chromium) and the lines 6px further apart
          // than the copy asks for. Matching it to the copy makes the first line the copy's own,
          // which is what CONTROL_OFFSET assumes. `inherit` reads the root declaration above.
          ".chakra-checkbox__label": { fontSize: "inherit", lineHeight: LINE_HEIGHT },
          ".chakra-checkbox__control": { marginTop: CONTROL_OFFSET },
        }}
      >
        {/*
         * The copy carries the styling, not the Checkbox: Chakra's Checkbox theme gives its own
         * label part a font size, so a `fontSize` on the Checkbox is overridden rather than
         * inherited. This is the child we own, and a declaration on the element itself beats the
         * inherited one - no reaching into Chakra's internal part classes.
         *
         * Also not Chakra's `size` prop, which would reach the label but shrink the control box
         * with it: that box is the tap target for a consent action, so it keeps its full size
         * however quiet the copy around it gets.
         */}
        <Text as="span" data-testid="tos-consent-copy" {...copyStyles}>
          <PendingLegalCopy linkProps={linkPropsFor(copyStyles)} />
        </Text>
      </Checkbox>
      <FormErrorMessage pl={8}>{error}</FormErrorMessage>
    </FormControl>
  );
};

export default ConsentView;
